import React from 'react';
import { X } from 'lucide-react';
import MemberPhoto from './MemberPhoto';
import { eur } from './CartFooter';

/**
 * Club_CartHeader — cabecera del panel de carrito del POS.
 *
 * Contrapartida en código del componente de Figma (3196:25166) y sus tres
 * variantes de la propiedad `Property 1`:
 *
 *   dispensario   «Carrito Dispensario»
 *   Barra         «Carrito Barra»
 *   Todo          «Dispensario + Barra»   (el socio tiene líneas en las dos)
 *
 * El título es lo ÚNICO que cambia entre variantes, así que aquí es un `tipo`
 * y no tres componentes.
 *
 * Sobre la etiqueta del saldo: el frame ponía «Disponible», pero el saldo
 * negativo es un estado válido en el club —se puede retirar debiendo— así que
 * «disponible» se contradice con la cifra en cuanto baja de cero. Dice «Saldo»,
 * igual que la ficha del socio y el pie del carrito.
 */

const TITULOS = {
  dispensario: 'Carrito Dispensario',
  barra: 'Carrito Barra',
  todo: 'Dispensario + Barra',
};

export default function CartHeader({
  tipo = 'dispensario',
  /** Socio dueño del carrito. Sin socio no se pinta la ficha, solo el título. */
  member = null,
  /** Saldo del socio. Puede ser negativo. */
  balance = null,
  onClose,
  className = '',
}) {
  const negativo = Number(balance) < 0;

  return (
    <div
      data-testid="cart-header"
      data-tipo={tipo}
      className={`flex flex-col gap-4 border-b border-[var(--border)] px-4 pt-3 pb-[13px] ${className}`}
    >
      <div className="flex items-start justify-between gap-2">
        <h2 className="text-sm font-bold text-[var(--foreground)]">
          {TITULOS[tipo] || TITULOS.dispensario}
        </h2>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar carrito"
            className="-m-1 flex-shrink-0 rounded-sm p-1 text-[var(--muted-foreground)] transition-colors hover:text-[var(--foreground)]"
          >
            <X size={16} strokeWidth={1.5} />
          </button>
        )}
      </div>

      {member && (
        <div className="flex h-10 items-center gap-3">
          <MemberPhoto
            path={member.documents_path || member.image_path}
            name={member.name}
            className="h-10 w-10 flex-shrink-0 rounded-md object-cover"
          />
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-bold text-[var(--foreground)]">
              {member.name}
            </div>
            <div className="truncate text-xs text-[var(--muted-foreground)]">
              Socio #{member.member_number ?? member.number} · {member.status || 'Activo'}
            </div>
          </div>
          {balance != null && (
            <div className="flex-shrink-0 text-right">
              {/* Text-xs/Overline del sistema: 8px con tracking amplio. */}
              <div className="text-[8px] uppercase tracking-[0.1em] text-[var(--muted-foreground)]">
                Saldo
              </div>
              {/* El rojo va en la CIFRA, no en un contenedor: es convención
                  contable para un número negativo, no una advertencia. */}
              <div
                className={`text-sm font-bold tabular-nums ${
                  negativo ? 'text-[var(--destructive)]' : 'text-[var(--foreground)]'
                }`}
              >
                {eur(balance)}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
